import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ScanEye, ImageUp, Upload, Loader2, Download, Shield, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/labs`;

function DropZone({ onFile, file, accept }) {
  const [dragOver, setDragOver] = useState(false);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <label
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`flex flex-col items-center justify-center h-36 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
        dragOver ? 'border-cyan-400/60 bg-cyan-500/5' : file ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-slate-700/40 bg-slate-900/30 hover:border-slate-600/60'
      }`}
    >
      <input type="file" accept={accept || 'image/*'} className="hidden" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
      {file ? (
        <>
          <ImageUp className="w-6 h-6 text-emerald-400 mb-2" />
          <span className="text-xs text-emerald-400 font-medium">{file.name}</span>
          <span className="text-[10px] text-slate-500 mt-0.5">{(file.size / 1024).toFixed(0)} KB — Click to change</span>
        </>
      ) : (
        <>
          <Upload className="w-6 h-6 text-slate-500 mb-2" />
          <span className="text-xs text-slate-400">Drop an image here or click to upload</span>
          <span className="text-[10px] text-slate-600 mt-0.5">JPG, PNG, WebP</span>
        </>
      )}
    </label>
  );
}

function DetectorTool() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleFile = (f) => {
    setFile(f);
    setResult(null);
    setError('');
    setPreview(URL.createObjectURL(f));
  };

  const detect = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API}/detect`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setResult(data.result);
    } catch (e) {
      setError(e.message || 'Detection failed');
    }
    setLoading(false);
  };

  const getVerdict = () => {
    if (!result?.output) return null;
    const output = result.output;
    if (typeof output === 'object' && output.confidence != null) {
      const conf = Math.round(output.confidence * 100);
      const isAI = output.is_ai_generated || conf > 50;
      return { conf, isAI, text: isAI ? 'Likely AI-Generated' : 'Likely Authentic' };
    }
    if (typeof output === 'string') {
      return { text: output, conf: null, isAI: null };
    }
    return { text: JSON.stringify(output), conf: null, isAI: null };
  };

  const verdict = result ? getVerdict() : null;

  return (
    <div className="p-5 rounded-2xl border border-slate-800/40 bg-slate-900/20" data-testid="detector-tool">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
          <ScanEye className="w-5 h-5 text-rose-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white">AI Image Detector</h2>
          <p className="text-[10px] text-slate-500">Detect whether an image was created by AI or is authentic</p>
        </div>
      </div>
      <div className="mt-4">
        <DropZone onFile={handleFile} file={file} />
      </div>
      {preview && (
        <div className="mt-3 flex justify-center">
          <img src={preview} alt="Preview" className="max-h-40 rounded-lg border border-slate-800/40 object-contain" />
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={detect} disabled={!file || loading} className="bg-rose-600 hover:bg-rose-500 text-white text-sm" data-testid="detect-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Analyzing...</> : 'Detect AI Image'}
        </Button>
      </div>
      {error && <p className="mt-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</p>}
      {verdict && (
        <div className="mt-4 p-4 rounded-xl border border-slate-800/30 bg-slate-950/50" data-testid="detect-result">
          <div className="text-center">
            {verdict.conf != null ? (
              <>
                <div className={`text-3xl font-black mb-1 ${verdict.isAI ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {verdict.conf}%
                </div>
                <div className={`text-sm font-semibold ${verdict.isAI ? 'text-rose-300' : 'text-emerald-300'}`}>
                  {verdict.text}
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-300">{verdict.text}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UpscaleTool() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleFile = (f) => {
    setFile(f);
    setResult(null);
    setError('');
    setPreview(URL.createObjectURL(f));
  };

  const upscale = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API}/upscale`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setResult(data.result);
    } catch (e) {
      setError(e.message || 'Upscale failed');
    }
    setLoading(false);
  };

  const outputUrl = result?.output_url || result?.output;

  return (
    <div className="p-5 rounded-2xl border border-slate-800/40 bg-slate-900/20" data-testid="upscale-tool">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
          <ImageUp className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white">Super Resolution (4K Upscaler)</h2>
          <p className="text-[10px] text-slate-500">Enhance low-resolution images to high-resolution using AI</p>
        </div>
      </div>
      <div className="mt-4">
        <DropZone onFile={handleFile} file={file} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={upscale} disabled={!file || loading} className="bg-violet-600 hover:bg-violet-500 text-white text-sm" data-testid="upscale-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Enhancing...</> : 'Upscale Image'}
        </Button>
      </div>
      {error && <p className="mt-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</p>}
      {outputUrl && (
        <div className="mt-4" data-testid="upscale-result">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Before / After</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-800/40 overflow-hidden">
              <p className="text-[9px] text-slate-600 text-center py-1 bg-slate-900/60">Original</p>
              {preview && <img src={preview} alt="Original" className="w-full object-contain max-h-52" />}
            </div>
            <div className="rounded-lg border border-violet-500/30 overflow-hidden">
              <p className="text-[9px] text-violet-400 text-center py-1 bg-violet-500/5">Enhanced</p>
              <img src={outputUrl} alt="Enhanced" className="w-full object-contain max-h-52" />
            </div>
          </div>
          <div className="mt-3 text-center">
            <a href={outputUrl} download target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
              data-testid="download-enhanced-btn">
              <Download className="w-4 h-4" /> Download Enhanced Image
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LabsPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="labs-page">
      <div className="max-w-4xl mx-auto px-4 py-8">

        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mb-8 transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Library
        </Link>

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-[2px]">Powered by NextXus</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-3" data-testid="labs-title" spellCheck={false}>
            NextXus Library Labs
          </h1>
          <p className="text-base text-slate-400 mb-4">Enhance Your Memory</p>
          <p className="text-sm text-slate-500 max-w-xl mx-auto leading-relaxed">
            Free AI-powered verification and archival tools for the Federation. Detect AI-generated images and enhance your visual archives to 4K resolution.
          </p>
          <div className="mt-4">
            <Badge className="text-[9px] border-0 px-3 py-1" style={{ backgroundColor: '#32CD3220', color: '#32CD32' }}>
              <Shield className="w-3 h-3 mr-1 inline" /> Free for all NextXus Citizens
            </Badge>
          </div>
        </div>

        {/* Tool Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
          <DetectorTool />
          <UpscaleTool />
        </div>

        {/* About */}
        <div className="text-center text-[10px] text-slate-600">
          NextXus Library Labs &middot; DeepAI-powered &middot; Cathedral Architecture v2.2 &middot; The 2226 Standard
        </div>
      </div>
    </div>
  );
}
